import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Package, Truck, Settings, LayoutDashboard, LogOut, User } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useShopify } from './providers/ShopifyProvider';

// Pages
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import ApproveQuote from './pages/ApproveQuote';
import TipDriver from './pages/TipDriver';
import TrackDeliveryDetailed from './pages/TrackDeliveryDetailed';
import ProductionConfirm from './pages/ProductionConfirm';
import SettingsPage from './pages/Settings';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Setup from './pages/Setup';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, authenticateWithSessionToken } = useAuth();
  const { isEmbedded, loading: shopifyLoading, sessionToken } = useShopify();
  const [authError, setAuthError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(false);

  // In embedded mode, authenticate with session token when it becomes available
  useEffect(() => {
    if (isEmbedded && sessionToken && !user && !authenticating) {
      setAuthenticating(true);
      authenticateWithSessionToken(sessionToken)
        .catch((error) => {
          console.error('Failed to authenticate with session token:', error);
          setAuthError('Failed to authenticate. Please try reloading the app.');
        })
        .finally(() => setAuthenticating(false));
    }
  }, [isEmbedded, sessionToken, user, authenticating, authenticateWithSessionToken]);

  // In embedded mode, wait for both Shopify and auth to be ready
  if (loading || (isEmbedded && (shopifyLoading || authenticating))) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  // Show error if authentication failed in embedded mode
  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{authError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-uber-green text-white rounded-lg hover:bg-green-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // In standalone mode, redirect to login if not authenticated
  if (!user && !isEmbedded) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Embedded layout for Shopify admin (no sidebar - Shopify provides navigation)
function EmbeddedLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="p-6">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}

// Standalone layout with sidebar
function AppLayout() {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-uber-green" />
            UberShipConnect
          </h1>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            <li>
              <Link
                to="/"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/'
                    ? 'bg-uber-green text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
                Dashboard
              </Link>
            </li>
            <li>
              <Link
                to="/orders"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  location.pathname.startsWith('/orders')
                    ? 'bg-uber-green text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Package className="w-5 h-5" />
                Orders
              </Link>
            </li>
            <li>
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-uber-green text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
            </li>
          </ul>
        </nav>

        {/* User info and logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Log out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { isEmbedded } = useShopify();

  // Public customer-facing pages (no auth required)
  const publicPaths = [
    '/dashboard/approve',
    '/dashboard/tip',
    '/dashboard/track',
    '/dashboard/production',
    '/login',
    '/forgot-password',
    '/setup',
  ];

  const isPublicPage = publicPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isPublicPage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Auth pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/setup" element={<Setup />} />

          {/* Customer-facing pages */}
          <Route path="/dashboard/approve/:orderId" element={<ApproveQuote />} />
          <Route path="/dashboard/tip/:orderId" element={<TipDriver />} />
          <Route path="/dashboard/track/:deliveryId" element={<TrackDeliveryDetailed />} />
          <Route path="/dashboard/production/:orderId" element={<ProductionConfirm />} />
        </Routes>
      </div>
    );
  }

  // Protected dashboard - use EmbeddedLayout when in Shopify, AppLayout when standalone
  return (
    <ProtectedRoute>
      {isEmbedded ? <EmbeddedLayout /> : <AppLayout />}
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
